// search-layer/composables/useSearch.ts

export const useSearch = () => {
  const queryStore = useSearchQueryStore()
  const uiStore = useSearchUIStore()

  /**
   * @param locationId - Location ID (required)
   * @param groupId - Optional group ID for group-specific search
   * @param type - Optional type filter (post/comment/all)
   * @returns TanStack Query object with search results
   */
  const createSearchQuery = (
    locationId: MaybeRefOrGetter<string | undefined>,
    groupId: MaybeRefOrGetter<string | undefined> = undefined,
    type: MaybeRefOrGetter<'post' | 'comment' | 'all' | undefined> = undefined
  ) => {
    return queryStore.createSearchQuery(
      computed(() => uiStore.query),
      locationId,
      groupId,
      type
    )
  }

  /**
   * @param query - Search query string
   * @param locationId - Location ID (required)
   * @param groupId - Optional group ID
   * @param type - Optional type filter
   * @returns TanStack Query object with search results
   */
  const createCustomSearchQuery = (
    query: MaybeRefOrGetter<string>,
    locationId: MaybeRefOrGetter<string | undefined>,
    groupId: MaybeRefOrGetter<string | undefined> = undefined,
    type: MaybeRefOrGetter<'post' | 'comment' | 'all' | undefined> = undefined
  ) => {
    return queryStore.createSearchQuery(query, locationId, groupId, type)
  }

  /**
   * @param locationId - Location ID (required)
   * @param groupId - Optional group ID for group-specific search
   * @param channelId - Optional channel ID for channel-specific search
   * @param type - Optional type filter (post/comment/all)
   * @returns TanStack Infinite Query object with paginated search results
   */
  const createInfiniteSearchQuery = (
    locationId: MaybeRefOrGetter<string | undefined>,
    groupId: MaybeRefOrGetter<string | undefined> = undefined,
    channelId: MaybeRefOrGetter<string | undefined> = undefined,
    type: MaybeRefOrGetter<'post' | 'comment' | 'all' | undefined> = undefined
  ) => {
    return queryStore.createInfiniteSearchQuery(
      computed(() => uiStore.query),
      locationId,
      groupId,
      channelId,
      type
    )
  }

  /**
   * @param query - Search query string
   * @param locationId - Location ID (required)
   * @param groupId - Optional group ID
   * @param channelId - Optional channel ID
   * @param type - Optional type filter
   * @returns TanStack Infinite Query object with paginated search results
   */
  const createCustomInfiniteSearchQuery = (
    query: MaybeRefOrGetter<string>,
    locationId: MaybeRefOrGetter<string | undefined>,
    groupId: MaybeRefOrGetter<string | undefined> = undefined,
    channelId: MaybeRefOrGetter<string | undefined> = undefined,
    type: MaybeRefOrGetter<'post' | 'comment' | 'all' | undefined> = undefined
  ) => {
    return queryStore.createInfiniteSearchQuery(query, locationId, groupId, channelId, type)
  }

  /**
   * @param searchQuery - Search query string (can be from route.query.query)
   * @param locationId - Location ID (required)
   * @param groupId - Optional group ID
   * @param channelId - Optional channel ID
   * @param type - Optional type filter
   * @returns Object with posts, loading states, and actions
   */
  const createSearchForPosts = (
    searchQuery: MaybeRefOrGetter<string>,
    locationId: MaybeRefOrGetter<string | undefined>,
    groupId: MaybeRefOrGetter<string | undefined> = undefined,
    channelId: MaybeRefOrGetter<string | undefined> = undefined,
    type: MaybeRefOrGetter<'post' | 'comment' | 'all' | undefined> = undefined
  ) => {
    // Create infinite search query
    const infiniteQuery = queryStore.createInfiniteSearchQuery(
      searchQuery,
      locationId,
      groupId,
      channelId,
      type
    )
    const posts = computed(() => {
      const queryValue = toValue(searchQuery)

      // Return empty if no search query
      if (!queryValue || queryValue.trim().length === 0) {
        return []
      }

      // Return empty if no data yet
      if (!infiniteQuery.data.value?.pages) {
        return []
      }

      // Flatten all pages from infinite query
      const allSearchResults = infiniteQuery.data.value.pages.flat()

      // Transform each result to Post format
      return allSearchResults.map(
        (result) =>
          ({
            ...result,
            // Ensure all required Post fields are present
            _id: result._id,
            id: result._id,
            title: result.title || '',
            content: result.content || '',
            author: result.author as any, // Type assertion - SearchAuthor compatible with Post author
            createdAt: result.createdAt,
            updatedAt: result.updatedAt,
            reactions: {
              LIKE: result.reactions?.LIKE || 0,
              COMMENT: result.reactions?.COMMENT || 0,
              LOVE: 0,
              LAUGH: 0,
              ANGRY: 0,
              SAD: 0,
              WOW: 0,
            },
            commentsCount: result.commentsCount || 0,
            // Preserve search-specific data
            highlightText: result.highlightText || [],
            comments:
              result.comments?.map((c) => ({
                ...c,
                author: c.author as any,
              })) || [],
            // Flag for search result rendering
            isSearchResult: true,
            searchQuery: queryValue,
          }) as any
      )
    })

    return {
      posts,
      isLoading: computed(() => infiniteQuery.isLoading.value),
      error: computed(() => infiniteQuery.error.value),
      hasNextPage: computed(() => infiniteQuery.hasNextPage.value),
      isFetchingNextPage: computed(() => infiniteQuery.isFetchingNextPage.value),

      // Actions
      loadMore: () => {
        if (infiniteQuery.hasNextPage.value && !infiniteQuery.isFetchingNextPage.value) {
          infiniteQuery.fetchNextPage()
        }
      },
      refetch: () => infiniteQuery.refetch(),

      // Raw query for advanced usage
      infiniteQuery,
    }
  }

  return {
    // Stores for direct access
    queryStore,
    uiStore,

    // Query creation functions
    createSearchQuery,
    createCustomSearchQuery,

    // Infinite scroll query creation functions
    createInfiniteSearchQuery,
    createCustomInfiniteSearchQuery,

    // PostsList integration
    createSearchForPosts,
  }
}

import { computed, toValue, type MaybeRefOrGetter } from 'vue'
import { useSearchQueryStore } from '../stores/search.query-store'
import { useSearchUIStore } from '../stores/search.ui-store'
